import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { collegesAPI, applicationsAPI } from '../utils/api';
import { BookOpen, Clock, CheckCircle, XCircle, FileText, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const MyColleges = () => {
  const [myColleges, setMyColleges] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadColleges();
  }, []);

  const loadColleges = async () => {
    try {
      const [colleges, apps] = await Promise.all([
        collegesAPI.getSaved(),
        applicationsAPI.getAll()
      ]);
      setMyColleges(colleges);
      setApplications(apps);
    } catch (error) {
      console.error('Error loading colleges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCollege = async (collegeId) => {
    try {
      await collegesAPI.unsave(collegeId);
      loadColleges();
      toast({
        title: 'Removed',
        description: 'College removed from your list',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleSubmitApplication = async (applicationId) => {
    try {
      await applicationsAPI.submit(applicationId);
      loadColleges();
      toast({
        title: 'Success!',
        description: 'Application submitted successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'submitted': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'not_started': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'submitted': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'in_progress': return <FileText className="h-5 w-5 text-yellow-600" />;
      case 'not_started': return <Clock className="h-5 w-5 text-gray-600" />;
      default: return <Clock className="h-5 w-5 text-gray-600" />;
    }
  };

  const notStarted = applications.filter(a => a.status === 'not_started');
  const inProgress = applications.filter(a => a.status === 'in_progress');
  const submitted = applications.filter(a => a.status === 'submitted');

  const CollegeCard = ({ college, application }) => (
    <Card key={college._id} className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex gap-4">
          <img
            src={college.image}
            alt={college.name}
            className="w-32 h-32 rounded-lg object-cover"
          />
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-xl font-semibold mb-1">{college.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{college.location}</p>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className={getStatusColor(application.status)}>
                    {application.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                  <Badge variant="outline">
                    Deadline: {new Date(college.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </Badge>
                </div>
              </div>
              {getStatusIcon(application.status)}
            </div>

            {application.status !== 'submitted' && (
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Application Progress</span>
                  <span className="font-medium">{application.progress}%</span>
                </div>
                <Progress value={application.progress} className="h-2" />
              </div>
            )}

            {application.status === 'submitted' && application.submitted_date && (
              <div className="mb-4">
                <p className="text-sm text-green-600">
                  ✓ Submitted on {new Date(application.submitted_date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            )}

            <div className="flex gap-2">
              {application.status === 'not_started' && (
                <Link to={`/application?app_id=${application._id}`}>
                  <Button size="sm">Start Application</Button>
                </Link>
              )}
              {application.status === 'in_progress' && (
                <>
                  <Link to={`/application?app_id=${application._id}`}>
                    <Button size="sm">Continue</Button>
                  </Link>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleSubmitApplication(application._id)}
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Submit
                  </Button>
                </>
              )}
              {application.status === 'submitted' && (
                <Button size="sm" variant="outline" disabled>
                  View Status
                </Button>
              )}
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => handleRemoveCollege(college._id)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Colleges</h1>
          <p className="text-gray-600">Manage your college applications</p>
        </div>

        {myColleges.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No colleges added yet</h3>
              <p className="text-gray-600 mb-6">Start by searching and adding colleges to your list</p>
              <Link to="/colleges">
                <Button>Browse Colleges</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="all" className="space-y-6">
            <TabsList>
              <TabsTrigger value="all">All ({myColleges.length})</TabsTrigger>
              <TabsTrigger value="not_started">Not Started ({notStarted.length})</TabsTrigger>
              <TabsTrigger value="in_progress">In Progress ({inProgress.length})</TabsTrigger>
              <TabsTrigger value="submitted">Submitted ({submitted.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-4">
              {myColleges.map(college => {
                const app = applications.find(a => a.college_id === college._id);
                return app ? <CollegeCard key={college._id} college={college} application={app} /> : null;
              })}
            </TabsContent>

            <TabsContent value="not_started" className="space-y-4">
              {notStarted.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-600">
                    No colleges in this category
                  </CardContent>
                </Card>
              ) : (
                notStarted.map(app => {
                  const college = myColleges.find(c => c._id === app.college_id);
                  return college ? <CollegeCard key={college._id} college={college} application={app} /> : null;
                })
              )}
            </TabsContent>

            <TabsContent value="in_progress" className="space-y-4">
              {inProgress.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-600">
                    No colleges in this category
                  </CardContent>
                </Card>
              ) : (
                inProgress.map(app => {
                  const college = myColleges.find(c => c._id === app.college_id);
                  return college ? <CollegeCard key={college._id} college={college} application={app} /> : null;
                })
              )}
            </TabsContent>

            <TabsContent value="submitted" className="space-y-4">
              {submitted.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-600">
                    No colleges in this category
                  </CardContent>
                </Card>
              ) : (
                submitted.map(app => {
                  const college = myColleges.find(c => c._id === app.college_id);
                  return college ? <CollegeCard key={college._id} college={college} application={app} /> : null;
                })
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default MyColleges;